import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PaginationButton from "@/components/ui/pagination-button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from "@/components/ui/table";
import { tableCellClass, tableCellLastRowClass, tableHeadClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import { Route as NonMedicalMasterRoute } from "@/routes/(inventory)/branch/_layout/inventory/non-medical-master/index";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon
} from "@radix-ui/react-icons";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { MoveRightIcon, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNonMedicalMasterData } from "../-hooks/useNonMedicalMasterData";
dayjs.extend(customParseFormat);

// TableCell and TableHead wrappers
const NMTableCell = ({ className = "", ...props }) => (
  <TableCell className={cn(tableCellClass, "text-sm font-medium py-0", className)} {...props} />
);
const NMTableHead = ({ className = "", ...props }) => (
  <TableHead className={cn(tableHeadClass, className)} {...props} />
);

const Component = () => {
  const searchParams = NonMedicalMasterRoute.useSearch();
  const { data, isLoading, isFetching } = useNonMedicalMasterData();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(searchParams.search || '');

  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!isFetching && isSearching) {
      setIsSearching(false);
    }
  }, [isFetching, isSearching]);

  const updateSearchParams = (updates: Partial<typeof searchParams>, showSearchLoading = false) => {
    if (showSearchLoading) {
      setIsSearching(true);
    }
    
    navigate({
      search: (prev: any) => ({ ...prev, ...updates }),
      replace: true, // This prevents creating new history entries
    });
  };

  const itemsPerPage = searchParams.pageSize;
  const totalPages = data?.totalCount
    ? Math.ceil(data.totalCount / itemsPerPage)
    : 1;

    const columns = [
      { key: "trade_name", label: "Trade Name" },
      { key: "unit", label: "Unit" },
      { key: "unit_type", label: "Unit Type" },
      { key: "barcode", label: "Barcode" },
      { key: "ddc_code", label: "DDC Code" },
      { key: "manufacturer", label: "Manufacturer" },
      { key: "owner", label: "Owner" },
      { key: "category", label: "Category" },
      { key: "sub_category", label: "Sub Category" },
      { key: "tax", label: "Tax" },
    ];
    
    const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {});

  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <Breadcrumb>
              <BreadcrumbList>
                  <BreadcrumbItem>
                      <BreadcrumbLink onClick={() => {
                        navigate({
                          to: '/inventory',
                        })
                      }} style={{
                        cursor: 'pointer'
                      }}>Inventory Overview</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                      <BreadcrumbPage>Non Medical Master</BreadcrumbPage>
                  </BreadcrumbItem>
              </BreadcrumbList>
          </Breadcrumb>
        </div>
        <hr className="my-4" />
        <div className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-x-2">
            <div className="relative w-72">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                <SearchIcon className="w-4 h-4" />
              </span>
              <Input
                placeholder="Search for item"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateSearchParams({
                      search: searchValue,
                      page: 1,
                    }, true);
                  }
                }}
                className="pl-8 pr-8 w-full"
              />
              {searchValue && (
                <Button
                  variant="ghost"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 h-6 w-6 text-gray-400 hover:text-gray-700"
                  onClick={() => {
                    setSearchValue("");
                    updateSearchParams({
                      search: "",
                      page: 1,
                    });
                  }}
                >
                  <span className="sr-only">Clear</span>
                  &#10005;
                </Button>
              )}
            </div>
          <Button
            variant="outline"
            disabled={isSearching || isLoading}
            onClick={() => {
              updateSearchParams({
                search: searchValue,
                page: 1,
              }, true);
            }}
          >
            {isSearching || isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                Searching...
              </div>
            )  : (
              'Search'
            )}
          </Button>
        </div>
          <div className="flex items-center gap-x-2 mr-2">
            <label htmlFor="pageSize" className="text-sm text-gray-600 whitespace-nowrap">
              Items per page:
            </label>
            <Select
              value={searchParams.pageSize?.toString() || '10'}
              onValueChange={(value) => {
                const newPageSize = parseInt(value);
                updateSearchParams({
                  pageSize: newPageSize,
                  page: 1,
                }, true);
              }}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="border rounded-lg bg-white overflow-hidden mt-4">
        {isLoading ? (
          <TableLoading columns={columns} visibleColumns={visibleColumns} />
        ) : !data || !data.nonMedicalMasters || data.nonMedicalMasters.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-gray-500">No entries found</div>
          </div>
        ) : (
          <>
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <NMTableHead>Trade Name</NMTableHead>
                <NMTableHead>Unit</NMTableHead>
                <NMTableHead>Unit Type</NMTableHead>
                <NMTableHead>Barcode</NMTableHead>
                <NMTableHead>Manufacturer</NMTableHead>
                <NMTableHead>Owner</NMTableHead>
                <NMTableHead>Category</NMTableHead>
                <NMTableHead>Sub Category</NMTableHead>
                <NMTableHead>Tax</NMTableHead>
                <NMTableHead></NMTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.nonMedicalMasters.map((item, index) => (
                <TableRow key={index}>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.tradeName}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.unit}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.unitType}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.barcode}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.metadata?.manufacturer ? item.metadata.manufacturer : "Not Available"}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.metadata?.owner ? item.metadata.owner : "Not Available"}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.metadata?.category ? item.metadata.category : "Not Available"}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.metadata?.subCategory ? item.metadata.subCategory : "Not Available"}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>{item.taxProfile?.applicableTaxes.filter(item => {
                                        const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                                        if (countryCode === 'IN') {
                                            return item.componentCode.includes('GST');
                                        } else if (countryCode === 'AE') {
                                            return item.componentCode.includes('VAT');
                                        }
                                        return false;
                                    }).map(a => {
                                        return `${a.componentCode} (${a.ratePercent}%)`;
                                    })}</NMTableCell>
                  <NMTableCell className={index === data.nonMedicalMasters.length - 1 ? tableCellLastRowClass : ""}>
                    <Button variant='link' onClick={() => {
                      navigate({
                        to: '/branch/inventory/item/$type/$itemId',
                        params: {
                          type: "NON_MEDICAL",
                          itemId: item.id
                        },
                        search: searchParams,
                      })
                    }}>
                      <MoveRightIcon className='w-4 h-4 mr-2' />
                      View Details
                    </Button>
                  </NMTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
            <p>{data?.totalCount} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  updateSearchParams({
                    page: 1
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  updateSearchParams({
                    page: searchParams.page -1
                  });
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((page, idx) => (
                  page === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={page}
                        active={searchParams.page === page}
                        onClick={() => {
                          updateSearchParams({
                            page: page as number
                          });
                        }}
                      >
                        <span className="text-sm">{page}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  updateSearchParams({
                    page: searchParams.page + 1
                  });
                }}
                disabled={data?.totalCount === 0 || searchParams.page === totalPages}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  updateSearchParams({
                    page: totalPages
                  });
                }}
                disabled={data?.totalCount === 0 ||searchParams.page === totalPages}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
            </div>
            </>
            )}
        </div>
      </div>
    </>
  );
};

export default Component;
